import { DrawUISingleton } from "./core/gui";
import { RBloomPass, RBlurPass, RCombinerPass, RPresentPass } from "./postprocess";

import {
    AsyncPixelReadingState,
    BindRenderTarget,
    CreateCubemapFramebuffers,
    CreateCubemapRT,
} from "./core/resourcesUtils";
import { SceneBase } from "./scenes/scene";
import { CheckGL } from "./core/shaderUtils";
import { AllocateCommonRenderingResources } from "./shaders/shaderConfig";

import { GUserInputDesc, InitUserInputEvents, UserInputUpdatePerFrame } from "./core/input";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { GetVec2, Vector2 } from "./core/types";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { GTime, MathAlignToPowerOf2, MathClamp, UpdateTime, showError } from "./core/utils";
import { ApplyCameraControl, SetupCameraControlThroughInput } from "./core/controller";
import { APP_ENVIRONMENT } from "../config/config";
import { GAudioEngine, SoundSample } from "./core/audioEngine";

import { GTexturePool } from "./core/texturePool";

import { GSimpleShapesRenderer } from "./helpers/shapeRender";
import { GRibbonsRenderer } from "./ribbons";
import { AllocateMainRenderTargets, GRenderTargets } from "./core/renderTargets";
import { GScreenDesc } from "./core/screenDesc";
import { GGlowSpriteRender } from "./backgroundScene";
import { VectorField, GVectorFieldInjector } from "./vectorField";
import { GBasePass, Mesh } from "./basePass";
import { PayneSceneLightSwitch } from "./scenes/payne/lightSwitch/lightSwitch";
import { GConvolutionPass } from "./lightProbes";
import { ApplyCanvasSettings, CanvasSettings, getCanvas } from "../components/Canvas/Canvas";
import { PayneSceneDestrPainting } from "./scenes/payne/destrPaintingMain/destrPaintingMain";
import {
    DestrPaintingCopyCommonMeshes,
    TransitionSceneToDestrPaintState,
} from "./scenes/payne/destrPaintingMain/destrPaintingScript";
import { PayneSceneHallway } from "./scenes/payne/hallway/hallwayMain";
import { TransitionSceneToHallway } from "./scenes/payne/hallway/hallwayScript";
import {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ESubSceneState,
    GLightSwitchSceneCSS,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    SetNewSubSceneState,
    TransitionSceneToLightSwitch,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    TransitionSceneToPhoneState,
} from "./scenes/payne/lightSwitch/lightSwitchScripts";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { GHallwaySceneCSS } from "./scenes/payne/hallway/hallwayCSS";
import { InitializeHotlineMeshes } from "./scenes/payne/lightSwitch/lightSwitchSceneDesc";
import { GSettings } from "./core/settings";

const GSimulationSettings = {
    bRunSimulation: true,
};

export const GPostProcessPasses: {
    CopyPresemt: RPresentPass | null;
    Blur: RBlurPass | null;
    Bloom: RBloomPass | null;
    Combiner: RCombinerPass | null;
    BloomNumBlurPasses: number;
    DesiredBloomTexSize: number;
} = {
    CopyPresemt: null,
    Blur: null,
    Bloom: null,
    Combiner: null,
    BloomNumBlurPasses: 3,
    DesiredBloomTexSize: 32, //64 //aligned to smallest screen side
};

//TODO: Backup texture for reallocation, to avoid flickering when window is resized
function SetupBloomPostProcessPass(gl: WebGL2RenderingContext) {
    const alignedRTSize = {
        x: MathAlignToPowerOf2(GScreenDesc.RenderTargetSize.x),
        y: MathAlignToPowerOf2(GScreenDesc.RenderTargetSize.y),
    };

    const RenderTargetMIPForBloom = Math.log2(
        (GScreenDesc.ScreenRatio < 1 ? alignedRTSize.x : alignedRTSize.y) / GPostProcessPasses.DesiredBloomTexSize,
    );

    const bloomTextureSize = {
        x: GScreenDesc.RenderTargetSize.x / Math.pow(2.0, RenderTargetMIPForBloom),
        y: GScreenDesc.RenderTargetSize.y / Math.pow(2.0, RenderTargetMIPForBloom),
    };

    if (GPostProcessPasses.Bloom) {
        const preBrCached = GPostProcessPasses.Bloom.PreBrightness;
        const preThres = GPostProcessPasses.Bloom.Threshold;
        GPostProcessPasses.Bloom = new RBloomPass(gl, bloomTextureSize, RenderTargetMIPForBloom);
        GPostProcessPasses.Bloom.PreBrightness = preBrCached;
        GPostProcessPasses.Bloom.Threshold = preThres;
    } else {
        GPostProcessPasses.Bloom = new RBloomPass(gl, bloomTextureSize, RenderTargetMIPForBloom);
    }
}

function SetupPostProcessPasses(gl: WebGL2RenderingContext) {
    GPostProcessPasses.CopyPresemt = new RPresentPass(gl);
    GPostProcessPasses.Blur = new RBlurPass(gl);
    GPostProcessPasses.Combiner = new RCombinerPass(gl);
    SetupBloomPostProcessPass(gl);
}

export function RenderMain() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const DEBUG_ENV = APP_ENVIRONMENT === "development";
    const DEBUG_UI = 1 && DEBUG_ENV;

    const canvas = getCanvas();

    GAudioEngine.Instance = new GAudioEngine();

    //=========================
    // 		SERVERS
    //=========================

    //if (!DEBUG_ENV)
    {
        GTexturePool.TextureLocationFolder = `https://payneassets.s3.eu-north-1.amazonaws.com/textures/`;
        SoundSample.GAudioLocationFolder = `https://payneassets.s3.eu-north-1.amazonaws.com/audio/`;
        Mesh.GMeshesLocationFolder = `https://payneassets.s3.eu-north-1.amazonaws.com/meshes/`;
    }

    //=========================
    // 		INPUT EVENTS
    //=========================
    InitUserInputEvents(canvas);

    if (DEBUG_ENV) {
        SetupCameraControlThroughInput();
    }

    //=========================
    // 	  WINDOW RESIZE INIT
    //=========================
    function GetWindowSizeCurrent(): Vector2 {
        const maxDPR = 1;
        const dpr = MathClamp(window.devicePixelRatio, 1, maxDPR);
        if (dpr > 1) {
            GRenderTargets.bUseHalfResRT = true;
        } else {
            GRenderTargets.bUseHalfResRT = false;
        }
        return { x: Math.round(window.innerWidth * dpr), y: Math.round(window.innerHeight * dpr) };
    }

    function OnWindowResize() {
        console.log("Window Resized");
        const newWindowSize = GetWindowSizeCurrent();
        GScreenDesc.WindowSize.x = newWindowSize.x;
        GScreenDesc.WindowSize.y = newWindowSize.y;
        canvas.width = GScreenDesc.WindowSize.x;
        canvas.height = GScreenDesc.WindowSize.y;
        canvas.style.width = window.innerWidth + "px";
        canvas.style.height = window.innerHeight + "px";
        canvas.style.display = "block";
        GScreenDesc.RenderTargetSize = { x: GScreenDesc.WindowSize.x, y: GScreenDesc.WindowSize.y };
        if (GSettings.EQuality == 1) {
            GScreenDesc.RenderTargetSize.x *= 0.75;
            GScreenDesc.RenderTargetSize.y *= 0.75;
        } else if (GSettings.EQuality == 0) {
            GScreenDesc.RenderTargetSize.x *= 0.5;
            GScreenDesc.RenderTargetSize.y *= 0.5;
        }
        GScreenDesc.ScreenRatio = window.innerWidth / window.innerHeight;
        GScreenDesc.bWideScreen = GScreenDesc.ScreenRatio > 1.0;
        GScreenDesc.ViewRatioXY = { x: 1.0, y: 1.0 };
        if (GScreenDesc.bWideScreen) {
            GScreenDesc.ViewRatioXY.x = window.innerWidth / window.innerHeight;
        } else {
            GScreenDesc.ViewRatioXY.y = window.innerHeight / window.innerWidth;
        }
    }

    // Call the resizeCanvas function initially and whenever the window is resized
    OnWindowResize();

    if (!canvas) {
        showError("Canvas Error");
    }

    /**
     * @type {WebGL2RenderingContext}
     */
    const gl = canvas.getContext("webgl2", { powerPreference: "high-performance", antialias: false });
    if (gl === null) {
        showError("WebGL context error");
        throw new Error("WebGL is not defined");
    }

    let ext = gl.getExtension("EXT_color_buffer_float");
    if (!ext) {
        ext = gl.getExtension("EXT_color_buffer_half_float");
        if (!ext) {
            showError("This device doesnt support FLOAT Framebuffers.");
            return;
        }
    }

    GTexturePool.ExtASTC = gl.getExtension("WEBGL_compressed_texture_astc");

    console.log(GTexturePool.ExtASTC);

    const deviceNotSupported = GTexturePool.ExtASTC != null && !GScreenDesc.bWideScreen;

    GHallwaySceneCSS.setDeviceSupport(!deviceNotSupported);

    if (DEBUG_ENV) {
        GTexturePool.InitTexColor = new Uint8Array([0, 0, 255, 255]);
    }

    GTexturePool.LoadGlobalTextures(gl);

    //==============================
    // 		ALLOCATE RESOURCES
    //==============================
    AllocateCommonRenderingResources(gl);

    //Allocate RenderTargets
    AllocateMainRenderTargets(gl);

    //GPU-CPU Readback Resources
    const GGpuReadData = {
        CurFireValueCPUArrBuffer: new Uint16Array(1),
        CurFireValueCPU: 0.0,
        ReadbackBuffer: gl.createBuffer(),
        InitReadbackBuffer: function () {
            gl.bindBuffer(gl.PIXEL_PACK_BUFFER, this.ReadbackBuffer);
            gl.bufferData(gl.PIXEL_PACK_BUFFER, this.CurFireValueCPUArrBuffer.byteLength, gl.STREAM_READ);
            gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
        },
        StateRef: { bReadingPixels: false } as AsyncPixelReadingState,
    };
    GGpuReadData.InitReadbackBuffer();

    //==============================
    // 		INIT RENDERERS
    //==============================

    GSimpleShapesRenderer.GInstance = new GSimpleShapesRenderer(gl);
    GRibbonsRenderer.GInstance = new GRibbonsRenderer(gl);

    SetupPostProcessPasses(gl);

    //Scene Global Uniform Buffer
    SceneBase.InitUniformBuffer(gl);

    GConvolutionPass.GInstance = new GConvolutionPass(gl);
    GBasePass.GInstance = new GBasePass(gl);

    GGlowSpriteRender.GInstance = new GGlowSpriteRender(gl);

    const vSize = 256;
    const ratio = GScreenDesc.ScreenRatio;
    const vfSize = GetVec2(vSize * ratio, vSize);
    const dSize = 1024.0;
    const densSize = GetVec2(dSize * ratio, dSize);
    VectorField.GFullscreenInstance = new VectorField(gl, vfSize, densSize, true);
    GVectorFieldInjector.GInstance = new GVectorFieldInjector(gl);

    const hallwayScene = new PayneSceneHallway(gl);
    PayneSceneHallway.GInstance = hallwayScene;

    const lightSwitchScene = new PayneSceneLightSwitch(gl);
    PayneSceneLightSwitch.GInstance = lightSwitchScene;
    //const destrPaintScene = new PayneSceneDestrPainting(gl);
    let destrPaintScene: PayneSceneDestrPainting | null = null;

    TransitionSceneToHallway(gl, hallwayScene);
    // eslint-disable-next-line prefer-const
    let curScene: SceneBase = hallwayScene;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, prefer-const
    let sceneNum = 0;

    {
        /* GHallwaySceneCSS.setStartButtonVis(false);
        InitializeHotlineMeshes(gl, lightSwitchScene);
        TransitionSceneToLightSwitch(gl, lightSwitchScene);
        curScene = lightSwitchScene;
        sceneNum = 1;
        SetNewSubSceneState(gl, ESubSceneState.Phone, lightSwitchScene);
        TransitionSceneToPhoneState(gl, lightSwitchScene);
        lightSwitchScene.bCustomEventFlag = 1; */
        /* {
            destrPaintScene = new PayneSceneDestrPainting(gl);
            DestrPaintingCopyCommonMeshes(lightSwitchScene, destrPaintScene);
            TransitionSceneToDestrPaintState(gl, destrPaintScene);
            curScene = destrPaintScene;
        } */
    }

    {
        //Load Env Map
        SceneBase.TextureEnvMapRect.InnerTexture = GTexturePool.CreateTexture(
            gl,
            false,
            "envmap9",
            true,
            true,
            false,
            SceneBase.TextureEnvMapRect,
        );
        SceneBase.TextureEnvMap = CreateCubemapRT(
            gl,
            SceneBase.TextureEnvMapSize,
            gl.R11F_G11F_B10F,
            gl.RGB,
            gl.HALF_FLOAT,
            false,
        );
        SceneBase.TextureEnvMapWithMips = CreateCubemapRT(
            gl,
            SceneBase.TextureEnvMapSize,
            gl.R11F_G11F_B10F,
            gl.RGB,
            gl.HALF_FLOAT,
            true,
        );
        SceneBase.TextureEnvMapFBO = CreateCubemapFramebuffers(gl, SceneBase.TextureEnvMap!);
    }

    //DebugReset Callback
    if (DEBUG_ENV) {
        document.addEventListener("keydown", function (event) {
            if (event.key == "r") {
                GVectorFieldInjector.GInstance?.InjectImpulse(
                    gl,
                    VectorField.GFullscreenInstance!,
                    GUserInputDesc.InputPosCurNDC,
                );
                //GCameraShakeController.ShakeCameraFast(2.1);
            }
        });
    }

    //===================
    // 		DEBUG UI
    //===================
    //let fpsElem: Element | null;
    if (DEBUG_UI) {
        const GDatGUI = DrawUISingleton.getInstance().getDrawUI();
        if (GDatGUI) {
            //For global vars
            {
                GDatGUI.add(GSimulationSettings, "bRunSimulation").name("Run Simulation");

                const folder = GDatGUI.addFolder("Main");

                folder.add(GTime, "DeltaMs").name("DeltaTime").listen().step(0.1);
                folder.add(GTime, "FPSAvrg").name("FPS").listen().step(1);
                //folder.add(GTime, "DeltaTimeScale", 0.0, 1.0).name("DTime Scale").listen().step(0.001);

                folder.add(GScreenDesc, "ScreenRatio").name("ScreenRatio").listen().step(0.01);
                folder.add(GScreenDesc.WindowSize, "y").name("Resolution").listen().step(0.01);

                {
                    folder
                        .add(CanvasSettings, "Brightness", 0, 3)
                        .name("Brightness")
                        .step(0.001)
                        .listen()
                        .onChange(function () {
                            ApplyCanvasSettings(canvas);
                        });
                    folder
                        .add(CanvasSettings, "Contrast", 0.9, 1.1)
                        .name("Contrast")
                        .step(0.001)
                        .onChange(function () {
                            ApplyCanvasSettings(canvas);
                        });
                    folder
                        .add(CanvasSettings, "Saturation", 0, 2)
                        .name("Saturation")
                        .step(0.001)
                        .onChange(function () {
                            ApplyCanvasSettings(canvas);
                        });
                }

                {
                    const ppFolder = GDatGUI.addFolder("PostProcess");
                    ppFolder.add(GPostProcessPasses, "BloomNumBlurPasses", 0, 10, 1).name("BloomNumBlurPasses");
                    if (GPostProcessPasses.Bloom) {
                        ppFolder
                            .add(GPostProcessPasses.Bloom, "IndexOfMipReturned", 1, 10, 1)
                            .name("BloomIndexOfMipReturned");
                        ppFolder.add(GPostProcessPasses.Bloom, "PreBrightness", 0, 10).name("BloomPreBrightness");
                        ppFolder
                            .add(GPostProcessPasses.Bloom, "Threshold", 0, 1)
                            .name("BloomThres")
                            .step(0.01)
                            .listen();
                        ppFolder.add(GPostProcessPasses.Bloom, "bUpsampleUseConstBlendWeight");
                        ppFolder
                            .add(GPostProcessPasses.Bloom, "UpsampleBlendWeight", 0, 1)
                            .name("UpsampleBlendWeight")
                            .step(0.01);
                    }
                    ppFolder.add(RCombinerPass, "Exposure", 0, 5).name("Exposure").step(0.01).listen();
                    ppFolder.add(RCombinerPass, "GammaCurve", 0, 3).name("GammaCurve").step(0.001);
                    ppFolder.add(RCombinerPass, "Brightness", 0, 5).name("Brightness").step(0.001);
                    ppFolder.add(RCombinerPass, "ColorContrast", 0.9, 1.1).name("Contrast").step(0.001);
                    ppFolder.add(RCombinerPass, "BloomStrength", 0, 2).name("BloomStrength").step(0.01);

                    ppFolder.add(RCombinerPass, "GradParamMin", -1.0, 1).name("ColorGradeParam").step(0.01);
                    ppFolder.add(RCombinerPass, "FisheyeParam", -0.25, 1.5).name("FisheyeParam").step(0.01);

                    //
                    const selectedOptions = {
                        selectedToneMapType: "None",
                    };
                    const tonemapTypes: string[] = [
                        "None",
                        "Reinhard",
                        "Reinhard Squared",
                        "Standard",
                        "Standard Old",
                        "ToneMap ACES",
                        "Uncharted 2 Tonemap",
                    ];
                    const tonemapTypeMap: Map<string, number> = new Map();
                    tonemapTypes.forEach((type, index) => {
                        tonemapTypeMap.set(type, index);
                    });
                    const controller = ppFolder
                        .add(selectedOptions, "selectedToneMapType", tonemapTypes)
                        .name("Tone Map Type");
                    controller.onChange((value) => {
                        const val = tonemapTypeMap.get(value);
                        if (val !== undefined) {
                            RCombinerPass.ETonemapper = val;
                            console.log("Tonemapper " + value + ` ` + val);
                        }
                    });
                    //
                    //ppFolder.add(RCombinerPass, "ETonemapper", ["Option 1", "Option 2", "Option 3"]).name("Selection");
                    const dispersFolder = ppFolder.addFolder("Abberation");
                    dispersFolder
                        .add(RCombinerPass, "DispersionOffsetScale", 0, 10)
                        .name("DispersionOffsetScale")
                        .step(0.01);
                    dispersFolder.add(RCombinerPass, "DispersionAmount", 0, 10).name("DispersionAmount").step(0.01);
                    dispersFolder
                        .add(RCombinerPass, "DispersionOffsetScaleBloom", 0, 10)
                        .name("DispersionOffsetScaleBloom")
                        .step(0.01);
                    dispersFolder
                        .add(RCombinerPass, "DispersionAmountBloom", 0, 10)
                        .name("DispersionAmountBloom")
                        .step(0.01);
                    ppFolder.add(RCombinerPass, "LensFlareScale", 0, 2).name("LensFlare").step(0.01);
                }
            }

            //GTexturePool.SubmitDebugUI(GDatGUI);
            VectorField.GFullscreenInstance?.SubmitDebugUI(GDatGUI);
            GVectorFieldInjector.GInstance.SubmitDebugUI(GDatGUI);
            curScene.SubmitDebugUI(GDatGUI);

            GDatGUI.close();
        }

        //deprecated fpsElem = document.querySelector("#fps");
        //enableFPSContainer();
    }

    //=============================================================================================================================
    //
    // 														RENDER LOOP
    //
    //=============================================================================================================================

    function RenderLoop() {
        if (gl !== null && GSimulationSettings.bRunSimulation) {
            //Debug UI
            if (DEBUG_ENV) {
                GTexturePool.LogTexturesInPool();
            }

            //=========================
            // 		WINDOW RESIZE
            //=========================
            const windowSizeCur = GetWindowSizeCurrent();
            if (
                canvas.width !== windowSizeCur.x ||
                canvas.height !== windowSizeCur.y ||
                GSettings.bQualityChangeThisFrame
            ) {
                //Resize
                OnWindowResize();
                AllocateMainRenderTargets(gl);
                SetupBloomPostProcessPass(gl);
            }

            UpdateTime();
            /* if (fpsElem) {
                fpsElem.textContent = GTime.FPSAvrg.toFixed(1);
            } */

            if (1) {
                UserInputUpdatePerFrame();

                ApplyCameraControl();

                //======================
                // 		SCENE
                //======================
                //Render to Env Map
                if (!SceneBase.bEnvMapProcessed && SceneBase.TextureEnvMapRect.bLoaded) {
                    //console.log("ENV MAP CONVERSION COMPLETE");
                    GConvolutionPass.GInstance!.ConvertRectnvToCubemap(
                        gl,
                        SceneBase.TextureEnvMapRect.InnerTexture!,
                        SceneBase.TextureEnvMapFBO!,
                        SceneBase.TextureEnvMapSize,
                    );
                    GConvolutionPass.GInstance!.GenerateRelfectedIrradianceCubemap(
                        gl,
                        SceneBase.TextureEnvMap!,
                        SceneBase.TextureEnvMapWithMips!,
                        SceneBase.TextureEnvMapFBO!,
                    );
                    SceneBase.bEnvMapProcessed = true;
                }

                curScene.Render(gl);

                if (sceneNum == 0) {
                    if (curScene.bCustomEventFlag == 1) {
                        InitializeHotlineMeshes(gl, lightSwitchScene);
                        curScene.bCustomEventFlag = 2;
                    }
                } else if (sceneNum == 1) {
                    if (curScene.bCustomEventFlag == 1) {
                        destrPaintScene = new PayneSceneDestrPainting(gl);
                        curScene.bCustomEventFlag = 2;
                    }
                }

                //=========================
                // 	VECTOR FIELD MAIN
                //=========================
                GVectorFieldInjector.GInstance!.InjectVelocityFromUserInput(
                    gl,
                    VectorField.GFullscreenInstance!,
                    0.95,
                    0.95,
                );
                VectorField.GFullscreenInstance!.UpdateMain(gl);

                //======================
                // 		BLOOM
                //======================

                if (GPostProcessPasses.Bloom!.BloomTexture !== null) {
                    //Downsample Source
                    GPostProcessPasses.Bloom!.HQBloomPrePass(gl, GRenderTargets.SceneColorTexture!);

                    GPostProcessPasses.Bloom!.HQBloomDownsample(gl);
                    GPostProcessPasses.Bloom!.HQBloomBlurAndUpsample(
                        gl,
                        GPostProcessPasses.BloomNumBlurPasses,
                        GPostProcessPasses.Blur!,
                    );
                }

                //======================
                // 		POST PROCESS
                //======================
                GPostProcessPasses.Combiner!.Execute(
                    gl,
                    GRenderTargets.SceneColorTexture!,
                    GPostProcessPasses.Bloom!.GetFinalTexture()!,
                    GRenderTargets.SmokeTexture!,
                    GRenderTargets.SpotlightTexture!,
                    null,
                    {
                        x: canvas.width,
                        y: canvas.height,
                    },
                );

                if (curScene.bSceneFinished) {
                    if (sceneNum == 0) {
                        TransitionSceneToLightSwitch(gl, lightSwitchScene);
                        curScene = lightSwitchScene;

                        sceneNum = 1;
                    } else {
                        if (destrPaintScene != null) {
                            BindRenderTarget(
                                gl,
                                GRenderTargets.SpotlightFramebuffer!,
                                GScreenDesc.RenderTargetSize,
                                true,
                                false,
                            );
                            RCombinerPass.bVolumeTextureReadEnabled = 0;
                            GLightSwitchSceneCSS.setResidenceSubVisibility(false);
                            DestrPaintingCopyCommonMeshes(lightSwitchScene, destrPaintScene);
                            TransitionSceneToDestrPaintState(gl, destrPaintScene);
                            curScene = destrPaintScene;
                        }
                    }
                }

                GSettings.bQualityChangeThisFrame = false;
            } else {
                gl.viewport(0, 0, canvas.width, canvas.height);
                gl.bindFramebuffer(gl.FRAMEBUFFER, null);

                gl.clearColor(0.0, 0.0, 0.0, 1.0);
                gl.clear(gl.COLOR_BUFFER_BIT);
            }

            if (gl !== null) {
                if (CheckGL(gl)) {
                    requestAnimationFrame(RenderLoop);
                }
            }
        }
    }

    if (gl !== null) {
        RenderLoop();
    }
}
